/** Hybrid assistant — rules + local LLM with shared context. */

import { askLocalLlm, isLlmEnabled, streamLocalLlm } from "assistant/llmClient";
import { buildPageAwareContext } from "assistant/pageAware";

export async function enhanceWithLlm(userMessage, context, ruleFacts, mode = "enhance", onUpdate) {
  if (!context?.aiRoutingEnabled || !isLlmEnabled()) return null;

  if (onUpdate) {
    const streamResult = await streamLocalLlm(
      userMessage,
      context,
      { mode, ruleFacts },
      onUpdate
    );
    if (streamResult?.ok && streamResult.reply) {
      return streamResult.reply;
    }
    return null;
  }

  const llmResult = await askLocalLlm(userMessage, context, {
    mode,
    ruleFacts,
  });

  if (llmResult?.ok && llmResult.reply) {
    return llmResult.reply;
  }
  return null;
}

/** Merge rule-engine UI (cards, actions) with LLM narrative text. */
export async function hybridizeRuleResponse(ruleResponse, userMessage, context, ruleFacts) {
  if (!ruleResponse || !isLlmEnabled()) {
    return ruleResponse;
  }

  const narrative = await enhanceWithLlm(userMessage, context, ruleFacts, "enhance");
  if (!narrative) {
    return ruleResponse;
  }

  return {
    ...ruleResponse,
    type: "hybrid",
    content: narrative,
    hybridMeta: {
      usedLlm: true,
      intent: ruleFacts?.intent,
    },
  };
}

export async function hybridOpenChat(userMessage, context, prefetchBundle) {
  const pageAware = buildPageAwareContext(context.pathname, context.pageContext, context.language);
  const ruleFacts = {
    intent: "open_chat",
    structuredText: prefetchBundle?.liveData || "",
    pageHelp: prefetchBundle?.pageHelp || null,
    facts: prefetchBundle?.facts || null,
    pageAware: pageAware?.summary ? pageAware : null,
  };

  return askLocalLlm(userMessage, context, {
    mode: "hybrid",
    ruleFacts,
  });
}

export function llmErrorMessage(llmResult, language) {
  const errDetail = llmResult?.detail || "";
  const errHints =
    language === "en"
      ? {
          disabled:
            "Local AI is off. Add REACT_APP_ASSISTANT_LLM_* to `.env`, then restart `npm start`.",
          auth: "Session expired — log out and log in again.",
          "401": "Auth failed — check API keys and restart both servers.",
          network: "Cannot reach assistant-server. Run `npm start` in `assistant-server/`.",
          http: `Assistant error: ${errDetail}`,
        }
      : {
          disabled:
            "IA locale désactivée. Ajoutez REACT_APP_ASSISTANT_LLM_* dans `.env`, puis redémarrez `npm start`.",
          auth: "Session expirée — déconnectez-vous et reconnectez-vous.",
          "401": "Échec auth — vérifiez les clés API et redémarrez les deux serveurs.",
          network: "assistant-server injoignable. Lancez `npm start` dans `assistant-server/`.",
          http: `Erreur assistant : ${errDetail}`,
        };

  return (
    errHints[llmResult?.error] ||
    errHints[String(llmResult?.status)] ||
    (String(errDetail).toLowerCase().includes("assistant key") ? errHints["401"] : null) ||
    errHints.http ||
    errDetail
  );
}
